// PDP Data Collection Utilities
import { getColorFromHash } from "common/utils/color-url-persist";

// DOM fallback: hidden input kept in sync with the active color by show-product.js
const getActiveColorFromDom = () =>
  document.getElementById("active-color-name")?.value?.trim() || null;

export const getActiveColorName = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const acParam = urlParams.get("ac");
  if (acParam) {
    const decodedColor = decodeURIComponent(acParam.replace(/\+/g, " "));
    return decodedColor;
  }

  // PIP-3358: on the canonical-URL surface the color lives in the DOM (canonical
  // casing) and the #color hash (lossy slug), not ?ac=. Prefer the DOM value so
  // analytics/design-lab resolve the exact, correctly-cased color; fall back to
  // the hash only when the DOM has no active color yet. Reading is deliberately
  // NOT flag-gated (only writing is): shared/bookmarked #color links must keep
  // resolving in flag-off sessions — mixed rollout states, rollback, and the
  // cache-TTL window after a toggle.
  return getActiveColorFromDom() || getColorFromHash();
};

const findQuantityInputs = () => {
  // Try multiple selectors to handle different contexts
  const selectors = [
    ".js-quantity-form-input", // Regular PDP (desktop)
    "input[name^='variants['][name$='][quantity]']", // Mobile PDP
    ".js-product-details-quantity-form input[data-variant-id]" // Vue.js context
  ];

  for (const selector of selectors) {
    const $inputs = document.querySelectorAll(selector);
    if ($inputs.length > 0) {
      return $inputs;
    }
  }

  return [];
};

const extractVariantId = input => {
  // Handle both desktop (data-variant-id) and mobile (name attribute) formats
  const { variantId } = input.dataset;

  // For mobile inputs, extract variant ID from name attribute: "variants[123][quantity]"
  if (!variantId && input.name) {
    const [, extractedId] = input.name.match(/variants\[(\d+)\]\[quantity\]/) || [];
    if (extractedId) {
      return extractedId;
    }
  }

  return variantId;
};

const processInput = ({ input, activeColor, quantities, sizeData }) => {
  const variantId = extractVariantId(input);
  const { color, size } = input.dataset;
  const value = parseInt(input.value, 10) || 0;

  // For single-color PDPs, collect all inputs with values regardless of color attribute
  // This handles cases where color is changed via JS but DOM isn't updated
  if (variantId && value > 0) {
    // If we have a color filter and it doesn't match, check if we're on a PDP
    // On PDPs, we show only one color at a time, so we can safely collect all inputs
    const isPDP = document.querySelector(".js-product-details-quantity-form");
    if (!color || color === activeColor || isPDP) {
      quantities[variantId] = value;
      if (size) {
        sizeData[variantId] = { size, quantity: value };
      }
    }
  }
};

export const collectPdpData = activeColor => {
  if (!activeColor) {
    return { quantities: null, sizeData: null };
  }

  const $form = document.querySelector(".js-product-details-quantity-form");
  if (!$form) {
    return { quantities: null, sizeData: null };
  }

  const quantities = {};
  const sizeData = {};
  const $inputs = findQuantityInputs();

  $inputs.forEach(input => {
    processInput({ input, activeColor, quantities, sizeData });
  });

  return {
    quantities: Object.keys(quantities).length > 0 ? quantities : null,
    sizeData: Object.keys(sizeData).length > 0 ? sizeData : null
  };
};

// Keep the original functions for backward compatibility
export const collectPdpQuantities = activeColor => collectPdpData(activeColor).quantities;

export const collectPdpSizeData = activeColor => collectPdpData(activeColor).sizeData;
